import { useState } from 'react';
import { AppHeader } from '../components/AppHeader';
import { ProgressHeader } from '../components/ProgressHeader';
import { TaskCard } from '../components/TaskCard';
import { ProfileModal } from '../components/ProfileModal';
import { useOnboardingProgress } from '../hooks/useOnboardingProgress';
import { useProfile } from '../hooks/useProfile';
import { useSectionStatus } from '../hooks/useSectionStatus';
import { IconUser, IconCircle, IconCircleHalf2, IconCheck } from '@tabler/icons-react';
import type { SectionStatus } from '../types/onboarding';

export function Dashboard() {
  const { profileData, isComplete: isProfileComplete, refreshProfile } = useProfile();
  const { activeSections, comingSoonSections } = useOnboardingProgress();
  const { getDataDrivenStatus, dataCompletedCount } = useSectionStatus();
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  const firstName = profileData.firstName || 'there';

  // Determine profile status based on completion
  const profileStatus: SectionStatus = isProfileComplete ? 'complete' : 'unseen';

  // Status icon configuration for profile card
  const profileStatusConfig = {
    unseen: { icon: IconCircle, color: 'text-gray-500', bg: 'bg-gray-800/30', border: 'border-gray-600/30' },
    'in-progress': { icon: IconCircleHalf2, color: 'text-amber-400', bg: 'bg-amber-900/30', border: 'border-amber-500/30' },
    complete: { icon: IconCheck, color: 'text-green-400', bg: 'bg-green-500/20', border: 'border-green-500/50' },
  };
  const StatusIcon = profileStatusConfig[profileStatus].icon;

  return (
    <div className="min-h-screen">
      <div className="flex flex-col items-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-4xl">
          <AppHeader showStatus statusText="Dashboard // Active" showUserMenu />

          {/* Welcome Message */}
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-100 mb-2">
              Welcome, {firstName}!
            </h2>
            <p className="text-gray-400">
              Complete the sections below to build your personalized health blueprint.
              You can complete them in any order.
            </p>
          </div>

          {/* Progress */}
          <ProgressHeader
            completedCount={(isProfileComplete ? 1 : 0) + dataCompletedCount}
            activeCount={activeSections.length + 1} // +1 for profile
          />

          {/* Profile Card */}
          <div
            onClick={() => setProfileModalOpen(true)}
            className={`card cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(0,214,245,0.15)] mb-4 ${
              profileStatus === 'complete' ? 'opacity-75' : ''
            }`}
          >
            <div className="flex items-center gap-4 p-4">
              <div
                className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  profileStatus === 'complete'
                    ? 'bg-green-900/30 border border-green-500/30'
                    : 'bg-cyan-900/30 border border-cyan-500/30'
                }`}
              >
                <IconUser
                  size={24}
                  className={profileStatus === 'complete' ? 'text-green-400' : 'text-cyan-400'}
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-100">Your Profile</h3>
                <p className="text-sm text-gray-400 mt-1">Name, contact information, and address</p>
              </div>
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 rounded-full ${profileStatusConfig[profileStatus].bg} border ${profileStatusConfig[profileStatus].border} flex items-center justify-center`}>
                  <StatusIcon size={20} className={profileStatusConfig[profileStatus].color} />
                </div>
              </div>
            </div>
          </div>

          <ProfileModal
            opened={profileModalOpen}
            onClose={() => setProfileModalOpen(false)}
            onSave={refreshProfile}
          />

          {/* Active Task Cards */}
          <div className="space-y-4 mb-8">
            {activeSections.map((section) => (
              <TaskCard
                key={section.id}
                section={section}
                status={getDataDrivenStatus(section.id)}
              />
            ))}
          </div>

          {/* Coming Soon Section */}
          {comingSoonSections.length > 0 && (
            <>
              <div className="flex items-center gap-4 my-8">
                <div className="flex-1 h-px bg-gray-800"></div>
                <span className="text-sm text-gray-600 uppercase tracking-wider">Coming Soon</span>
                <div className="flex-1 h-px bg-gray-800"></div>
              </div>
              <div className="space-y-4">
                {comingSoonSections.map((section) => (
                  <TaskCard
                    key={section.id}
                    section={section}
                    status="unseen"
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
